import { useI18n } from '@platform/i18n';
import { Badge } from '../components/badge';
import { Button } from '../components/button';
import { ConfirmDialog } from '../composites/confirm-dialog';
import { EmptyState, ErrorState } from '../composites/states';
import { Label } from '../components/label';
import { LockBadge, SettingsCard } from '../composites/settings';
import { MenuSelect } from '../components/menu-select';
import { Skeleton } from '../components/misc';
import { toast } from '../components/sonner';
import { sanitizeNoticeKey, SignatureEditor, SignaturePreview } from './signature-editor';
import { useAsync } from '../hooks/use-async';
import { useErrorText } from './errors';
import { useReportEdits } from '../composites/edit-boundary';
import { useDiscardGuard } from '../hooks/use-discard-guard';
import { PenLineIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useRef, useState } from 'react';
import type { MailboxSignature as MySignature, MailboxSignatures as MySignatures, MailboxSignatureAdapter } from './mailbox-settings-types';
/** Signature being edited: an existing personal one, or a new one (id undefined). */
interface Editing {
  id?: string;
  name: string;
  html: string;
  /** Name and stored HTML when the editor opened (the list carries no templates), for the dirty check. */
  baseName: string;
  baseHtml: string;
  key: number;
}

const NONE = 'none';

/** Parent must key this body by the immutable organization/mailbox target. */
export function MailboxSignaturesBody({ adapter, capabilities, title, onSaved }: {
  adapter: MailboxSignatureAdapter;
  capabilities: { write: boolean };
  title?: string;
  onSaved?: () => void;
}) {
  const { t } = useI18n();
  const errors = useErrorText();
  const state = useAsync(() => adapter.load());
  const [data, setData] = useState<MySignatures>();
  const [editing, setEditing] = useState<Editing>();
  const [viewing, setViewing] = useState<MySignature>();
  const [removing, setRemoving] = useState<MySignature>();
  const [nameError, setNameError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const current = data ?? state.data;
  const pending = useRef(false);
  const dirty = editing !== undefined && (editing.name !== editing.baseName || editing.html !== editing.baseHtml);
  useReportEdits(dirty, busy);
  const discard = useDiscardGuard({ dirty, busy, confirmation: {
    title: t('common.form.discardTitle'), description: t('common.form.discardDescription'), confirmLabel: t('common.form.discardConfirm'),
  }});

  /** Runs a change, keeps the returned list and tells the composer to reload. */
  const run = async (call: () => Promise<MySignatures>, done: string): Promise<boolean> => {
    if (pending.current || !capabilities.write) return false;
    pending.current = true;
    setBusy(true);
    try {
      setData(await call());
      onSaved?.();
      toast.success(done);
      return true;
    } catch (error) {
      errors.notify(error);
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  const save = async () => {
    if (!editing || busy || !capabilities.write) return;
    const name = editing.name.trim();
    if (name === '') {
      setNameError(t('webmail.signatures.nameRequired'));
      return;
    }
    const input = { name, html: editing.html };
    const ok = await run(async () => {
      const list = await (editing.id ? adapter.update(editing.id, input) : adapter.create(input));
      // The server sanitizes the HTML: tell the user when the saved version differs from what was typed.
      const saved = list.signatures.find((s) => s.id === editing.id) ?? [...list.signatures].reverse().find((s) => s.name === name);
      const notice = saved?.template === undefined ? null : sanitizeNoticeKey(input.html, saved.template);
      if (notice) toast.info(t(notice));
      return list;
    }, t(editing.id ? 'webmail.signatures.saved' : 'webmail.signatures.created'));
    if (ok) setEditing(undefined);
  };

  /** Opens the editor; an existing signature's stored HTML is fetched now (the list omits it to stay small). */
  const startEdit = async (signature?: MySignature) => {
    if (busy || pending.current || !capabilities.write || (signature && (signature.source !== 'personal' || signature.mandatory))) return;
    setViewing(undefined);
    setNameError(undefined);
    let html = '';
    if (signature) {
      pending.current = true;
      setBusy(true);
      try {
        html = (await adapter.get(signature.id)).template;
      } catch (error) {
        errors.notify(error);
        return;
      } finally {
        pending.current = false;
        setBusy(false);
      }
    }
    setEditing({ id: signature?.id, name: signature?.name ?? '', html, baseName: signature?.name ?? '', baseHtml: html, key: Date.now() });
  };

  const options = [{ value: NONE, label: t('webmail.signatures.none') }, ...(current?.signatures ?? []).map((s) => ({ value: s.id, label: s.name }))];
  const setDefault = (kind: 'new' | 'reply', value: string) => {
    if (!current || busy || editing !== undefined || !capabilities.write || current.locked[kind]) return;
    void run(() => adapter.setDefaults({ ...current.defaults, [kind]: value === NONE ? null : value }), t('webmail.signatures.defaultsSaved'));
  };

  if (state.error && !current) return <ErrorState description={errors.describe(state.error)} onRetry={state.reload} />;

  return (
    <>
        <SettingsCard
          title={title ?? t('webmail.signatures.listTitle')}
          actions={
            <Button onClick={() => void startEdit()} disabled={!current || editing !== undefined || busy || !capabilities.write}>
              <PlusIcon />
              {t('webmail.signatures.new')}
            </Button>
          }
        >
          {!current ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : current.signatures.length === 0 ? (
            <EmptyState icon={PenLineIcon} title={t('webmail.signatures.empty')} description={t('webmail.signatures.emptyDescription')} />
          ) : (
            <ul className="flex flex-col divide-y rounded-md border">
              {current.signatures.map((signature) => (
                <li key={signature.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate font-medium">{signature.name}</span>
                  {signature.source === 'central' && <Badge variant="info">{t('signatures.central')}</Badge>}
                  {signature.mandatory && <LockBadge label={t('webmail.signatures.mandatory')} />}
                  {signature.source === 'personal' && !signature.mandatory ? (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon-sm" aria-label={t('webmail.signatures.edit', { name: signature.name })} disabled={busy || !capabilities.write} onClick={() => discard.request(() => void startEdit(signature))}>
                        <PencilIcon />
                      </Button>
                      <Button variant="ghost" size="icon-sm" aria-label={t('webmail.signatures.delete', { name: signature.name })} disabled={busy || editing !== undefined || !capabilities.write} onClick={() => setRemoving(signature)}>
                        <Trash2Icon />
                      </Button>
                    </div>
                  ) : (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => discard.request(() => { setEditing(undefined); setViewing(signature); })}>
                      {t('webmail.signatures.view')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SettingsCard>

        {editing && (
          <SettingsCard title={t(editing.id ? 'webmail.signatures.editTitle' : 'webmail.signatures.newTitle')}>
            <fieldset disabled={busy || !capabilities.write} inert={busy || !capabilities.write} className="contents">
            <SignatureEditor
              key={editing.key}
              name={editing.name}
              onNameChange={(name) => { setNameError(undefined); setEditing((value) => (value ? { ...value, name } : value)); }}
              defaultHtml={editing.html}
              onHtmlChange={(html) => setEditing((value) => (value ? { ...value, html } : value))}
              nameError={nameError}
              placeholders
            />
            </fieldset>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void save()} disabled={busy || !capabilities.write}>
                {t('common.save')}
              </Button>
              <Button variant="outline" onClick={() => discard.request(() => setEditing(undefined))} disabled={busy}>
                {t('common.cancel')}
              </Button>
            </div>
          </SettingsCard>
        )}

        {viewing && (
          <SettingsCard title={viewing.name} description={t('webmail.signatures.centralReadOnly')} status={<Badge variant="info">{t('signatures.central')}</Badge>}>
            <SignaturePreview html={viewing.html} />
            <div>
              <Button variant="outline" onClick={() => setViewing(undefined)}>
                {t('common.close')}
              </Button>
            </div>
          </SettingsCard>
        )}

        {current && (
          <SettingsCard title={t('webmail.signatures.defaultsTitle')} description={t('webmail.signatures.defaultsDescription')}>
            {(['new', 'reply'] as const).map((kind) => {
              const label = t(kind === 'new' ? 'webmail.signatures.defaultNew' : 'webmail.signatures.defaultReply');
              return (
                <div key={kind} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
                  <Label className="sm:w-56">{label}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <MenuSelect label={label} value={current.defaults[kind] ?? NONE} options={options} onChange={(value) => setDefault(kind, value)} disabled={busy || editing !== undefined || !capabilities.write || current.locked[kind]} className="w-64 max-w-full" />
                    {current.locked[kind] && <LockBadge label={t('webmail.signatures.mandatory')} hint={t('webmail.signatures.lockedHint')} />}
                  </div>
                </div>
              );
            })}
          </SettingsCard>
        )}

      <ConfirmDialog
        open={removing !== undefined}
        onOpenChange={(open) => !open && setRemoving(undefined)}
        title={t('webmail.signatures.deleteTitle', { name: removing?.name ?? '' })}
        description={t('webmail.signatures.deleteDescription')}
        confirmLabel={t('webmail.signatures.deleteConfirm')}
        destructive
        onConfirm={async () => {
          if (!removing || removing.source !== 'personal' || removing.mandatory || busy || !capabilities.write) return;
          const id = removing.id;
          if (await run(() => adapter.remove(id), t('webmail.signatures.deleted'))) setRemoving(undefined);
        }}
      />
      {discard.prompt}
    </>
  );
}
